import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Loader2, Upload as UploadIcon } from "lucide-react";

// -----------------------------------------------------------------------------
// NfcDropZone — drag & drop for JSON/TXT exports from external NFC readers.
// Sends the raw text to `/reception/parse-nfc` which normalises it into the
// same shape as the visual OCR endpoint so downstream review is identical.
// Extracted from ReceptionTab.jsx (Iteration 62 — modularisation).
// -----------------------------------------------------------------------------
export default function NfcDropZone({ IT, onBack, onParsed, onBatchRegister, queue: extQueue, setQueue: extSetQueue }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // Controlled queue when parent passes it (so state survives review→nfc
  // navigation); otherwise fall back to internal state.
  const [localQueue, setLocalQueue] = useState([]);
  const queue = extQueue !== undefined ? extQueue : localQueue;
  const setQueue = extSetQueue || setLocalQueue;
  const [batch, setBatch] = useState(null); // { total, done, ok, err }
  const inputRef = useRef(null);

  const parseOne = async (file) => {
    if (!file) return null;
    if (file.size > 512 * 1024) {
      return { filename: file.name, error: IT ? "File > 512KB" : "File > 512KB" };
    }
    const text = await file.text();
    try {
      const r = await api.post("/reception/parse-nfc", { content: text, filename: file.name });
      return {
        filename: file.name,
        extracted: r.data.extracted || {},
        mrz_parsed: r.data.mrz_parsed || null,
        mrz_comparison: r.data.mrz_comparison || [],
      };
    } catch (e) {
      return { filename: file.name, error: errMsg(e, "Errore parse") };
    }
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const results = await Promise.all(files.map(parseOne));
      const ok = results.filter((r) => r && !r.error);
      const errs = results.filter((r) => r?.error);
      if (errs.length) toast.error(IT ? `${errs.length} file non parsabili` : `${errs.length} unparsable files`);
      // Single-file drop → skip preview, populate the review form immediately.
      if (ok.length === 1 && !queue.length) {
        toast.success(IT ? "Dati NFC importati" : "NFC data imported");
        onParsed(ok[0].extracted, ok[0].mrz_parsed, ok[0].mrz_comparison);
        return;
      }
      setQueue((q) => [...q, ...ok, ...errs]);
    } finally { setBusy(false); }
  };

  // Not a hook — plain callback. Named `pickRow` (not `useRow`) so the
  // React rules-of-hooks linter doesn't flag it when invoked inside .map().
  const pickRow = (idx) => {
    const row = queue[idx];
    if (!row || row.error) return;
    setQueue((q) => q.filter((_, i) => i !== idx));
    onParsed(row.extracted, row.mrz_parsed, row.mrz_comparison);
  };

  const clearQueue = () => setQueue([]);
  const validCount = queue.filter((r) => !r.error).length;

  const registerAll = async () => {
    const valid = queue.filter((r) => !r.error);
    if (!valid.length || !onBatchRegister) return;
    setBatch({ total: valid.length, done: 0, ok: 0, err: 0 });
    const res = await onBatchRegister(valid, (p) => {
      setBatch({ total: valid.length, done: p.i + 1, ok: p.ok, err: p.err });
    });
    // Drop the rows that were processed (keep errored ones for retry).
    setQueue([]);
    setBatch({ total: valid.length, done: valid.length, ok: res?.ok || 0, err: res?.err || 0, complete: true });
  };

  return (
    <div className="space-y-3" data-testid="nfc-dropzone-step">
      <div className="flex items-center justify-between text-xs">
        <span className="uppercase tracking-widest text-[10px] text-muted-foreground">{IT ? "Modalità" : "Mode"}: <span className="font-semibold text-sage">NFC</span></span>
        <button type="button" onClick={onBack} className="text-[11px] text-muted-foreground hover:text-primary underline">
          {IT ? "Cambia modalità" : "Change mode"}
        </button>
      </div>
      <input ref={inputRef} type="file" multiple accept=".json,.txt,.xml,application/json,text/plain,text/xml"
        onChange={(e) => handleFiles(e.target.files)} className="hidden" data-testid="nfc-file-input"/>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${dragOver ? "border-sage bg-sage/10" : "border-sage/40 bg-sage/5 hover:bg-sage/10"}`}
        data-testid="nfc-dropzone"
      >
        {busy ? (
          <><Loader2 className="w-8 h-8 mx-auto text-sage mb-2 animate-spin"/><div className="text-sm">{IT ? "Estrazione in corso…" : "Extracting…"}</div></>
        ) : (
          <>
            <UploadIcon className="w-8 h-8 mx-auto text-sage mb-2"/>
            <div className="text-sm font-medium">{IT ? "Trascina qui uno o più file NFC" : "Drop one or more NFC files"}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {IT ? "oppure clicca per selezionare · JSON · TXT · XML · multi-file supportato" : "or click to select · JSON · TXT · XML · multi-file supported"}
            </div>
            <div className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
              {IT
                ? "Trascinane più insieme per registrare tutta la famiglia in blocco — vedrai l'anteprima di ogni ospite prima del commit."
                : "Drop several to register the whole family at once — you'll preview each guest before committing."}
            </div>
          </>
        )}
      </div>

      {queue.length > 0 && (
        <div className="border border-border rounded-lg bg-card" data-testid="nfc-queue">
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border flex-wrap">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">
              {IT ? `Coda: ${validCount} ospiti pronti` : `Queue: ${validCount} guests ready`}
            </span>
            <div className="flex items-center gap-2">
              {validCount > 0 && (
                <button
                  type="button"
                  onClick={registerAll}
                  disabled={!!batch && !batch.complete}
                  className="text-[11px] uppercase tracking-widest px-3 py-1 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 lift disabled:opacity-50"
                  data-testid="nfc-batch-register-btn"
                >
                  {IT ? `Registra tutta la famiglia (${validCount})` : `Register whole family (${validCount})`}
                </button>
              )}
              <button type="button" onClick={clearQueue} className="text-[11px] text-muted-foreground hover:text-destructive underline">
                {IT ? "Svuota" : "Clear"}
              </button>
            </div>
          </div>

          {batch && (
            <div className="px-3 py-2 border-b border-border bg-primary/5" data-testid="nfc-batch-progress">
              <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
                <span>{IT ? "Registrazione in corso…" : "Registering…"}</span>
                <span className="font-mono">{batch.done}/{batch.total} · <span className="text-sage">✓ {batch.ok}</span> · <span className="text-destructive">✗ {batch.err}</span></span>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${Math.round((batch.done / batch.total) * 100)}%` }}/>
              </div>
              {batch.complete && (
                <div className="text-[10px] text-sage mt-1 uppercase tracking-widest">
                  {IT ? "Completato" : "Completed"}
                </div>
              )}
            </div>
          )}

          <div className="divide-y divide-border">
            {queue.map((row, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`nfc-queue-row-${i}`}>
                <div className="flex-1 min-w-0">
                  {row.error ? (
                    <div className="text-destructive">
                      <span className="font-mono text-[10px] mr-2">{row.filename}</span>
                      <span className="text-xs">{row.error}</span>
                    </div>
                  ) : (
                    <>
                      <div className="font-medium truncate">
                        {(row.extracted.first_name || "?")} {(row.extracted.last_name || "?")}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {row.extracted.date_of_birth || "—"} · {row.extracted.doc_type || "?"} {row.extracted.doc_number || ""} · <span className="font-mono">{row.filename}</span>
                      </div>
                    </>
                  )}
                </div>
                {!row.error && (
                  <button type="button" onClick={() => pickRow(i)}
                    className="text-[11px] uppercase tracking-widest px-3 py-1 rounded-full bg-sage text-white hover:bg-sage/90 lift"
                    data-testid={`nfc-queue-use-${i}`}>
                    {IT ? "Rivedi →" : "Review →"}
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="px-3 py-2 border-t border-border text-[10px] text-muted-foreground italic">
            {IT
              ? "\"Registra tutta la famiglia\" crea tutti gli ospiti in blocco senza aprire il form. \"Rivedi\" apre il modulo pre-compilato per correggere i dati prima del commit."
              : "\"Register whole family\" creates all guests at once without opening the form. \"Review\" opens the pre-filled form so you can fix data before commit."}
          </div>
        </div>
      )}
    </div>
  );
}
